// useVqeEditWiring — wires a VQE surface to the SR-Edit pipeline so the
// SELECT/DML renderers can light up edit affordances. One hook for every
// surface; the `adapter` says where the buffer lives. The compact popover,
// the Query Editor's Visual view and the dashboards tile builder
// (`useInlineToolbarEditContext`) write through a Monaco editor handle, so
// every edit is one undo stop on that editor's own stack.
//
// A per-tab `SqlResolverService` is created on first render and kept in a
// ref. The service buffer is kept in sync with `query` via `onTextChange` so
// SR-Edit can read the latest structure when popovers open.
//
// When the caller supplies a real `connectionId` + `database`, the service is
// keyed on that connection so it shares the autocomplete schema-cache /
// schema-bus invariants (DDL invalidation, per-connection snapshot store) with
// the rest of the renderer. `refreshSchemaSnapshot(database)` is kicked off on
// mount and re-fired on schema-bus updates so suggestion popovers see live
// schema for the active database. MetaInfo declaration writes for the same
// (connection, database) re-pull too: the snapshot folds the loose FKs (join
// candidates) and JSON declarations (column classification) in at pull time,
// and neither is a schema-bus event.
//
// `setSchemaSnapshot` is exposed via `__TEST_STORES__` so tests can seed
// suggestions without a live database.

import { useEffect, useMemo, useRef, useState } from 'react';

import { subscribe as subscribeToSchemaBus } from '../../../intellisense/schemaBus/schemaBus';
import { buildParsedDocumentFromSource } from '../../../sql-grammar/document';
import type { ParseOptions, ParsedDocument, SqlEngine } from '../../../sql-grammar/model';
import {
  createResolverService,
  type SqlResolverService,
} from '../../../sql-resolver/service/resolverService';
import type { SchemaSnapshot } from '../../../sql-resolver/types';
import { onMetaInfoDeclarationWritten } from '../../common/services/metaInfoApi';
import { isChipFilterPair } from '../../common/utils/chipFilterPair';
import type { MonacoEditorLike, MonacoTextModelLike } from './commitEdit';
import type { VqeEditContext } from './editContext';
import { buildLivePullSchemaSnapshot, sqlEngineToDatabaseEngineId } from './livePullSchemaSnapshot';
import { createSchemaPullScheduler } from './schemaPullScheduler';

function buildSync(source: string, options: ParseOptions): ParsedDocument {
  return buildParsedDocumentFromSource(source, options);
}

/** Where a surface's buffer lives. Callers memoise it (its identity keys the model adapter). */
export type VqeBufferAdapter = {
  /** SR-Edit document id prefix; the id is `${documentPrefix}${tabId}`. */
  readonly documentPrefix: string;
  /** Stub service connection id prefix for a tab with no connection. */
  readonly stubConnectionPrefix: string;
  /**
   * Model adapter for `commitEdit`. `getSource` returns the resolver's parsed
   * source — the LATEST text even between two chained commits in one click
   * handler, before React re-renders.
   */
  readonly createModel: (getSource: () => string) => MonacoTextModelLike;
  /** Undo-preserving apply path (Monaco `executeEdits`), when the buffer is a Monaco model. */
  readonly editor?: MonacoEditorLike;
  /**
   * Authoritative live buffer, read at commit time. `commitEdit` flushes the
   * resolver to it before resolving and guards against it moving mid-commit.
   */
  readonly getLiveSource: () => string | undefined;
};

export type UseVqeEditWiringArgs = {
  readonly tabId: string;
  readonly query: string;
  readonly statementId: string | null;
  readonly options: ParseOptions;
  /**
   * The active connection's id. Triggers schema-snapshot refresh against the
   * per-engine cache. When unset, the service uses a stub connection id and
   * the surface only lights up if a test injects a snapshot directly.
   */
  readonly connectionId?: string | null;
  /** Active database for schema refresh. Required to refresh; ignored otherwise. */
  readonly database?: string | null;
  readonly adapter: VqeBufferAdapter;
};

/**
 * `service` is ALWAYS present — created (and, when a connection is threaded,
 * kept schema-bus-synced) independently of whether a statement has been
 * classified, so a blank buffer can still read the table list for the starter
 * canvas. `editContext` is null until a statement is classified.
 */
export type VqeTabWiring = {
  readonly editContext: VqeEditContext | null;
  readonly service: SqlResolverService;
};

export function useVqeEditWiring(args: UseVqeEditWiringArgs): VqeTabWiring {
  const { tabId, query, statementId, options, connectionId, database, adapter } = args;
  const documentId = `${adapter.documentPrefix}${tabId}`;

  const serviceConnectionId = connectionId ?? `${adapter.stubConnectionPrefix}${tabId}`;
  const engine: SqlEngine = options.engine;

  // Service identity is keyed on (connectionId, engine). When either changes
  // we dispose and recreate so we never run a stale snapshot puller against a
  // new connection. `tabId` is appended to the registry key only — not to the
  // identity — because the tab id is stable for the lifetime of a tab.
  const serviceRef = useRef<SqlResolverService | null>(null);
  const serviceKeyRef = useRef<string | null>(null);
  const openedRef = useRef<string | null>(null);
  const disposedRef = useRef(false);
  const [, forceUpdate] = useState(0);

  const desiredKey = `${serviceConnectionId}|${engine}`;
  if (serviceKeyRef.current !== desiredKey) {
    if (serviceRef.current) {
      try {
        serviceRef.current.onDisconnect();
      } catch {
        /* idempotent dispose */
      }
      unregisterTabServiceForTests(tabId, serviceRef.current);
    }
    const pullSchemaSnapshot = connectionId
      ? buildLivePullSchemaSnapshot(connectionId, engine)
      : undefined;
    const engineId = sqlEngineToDatabaseEngineId(engine);
    serviceRef.current = createResolverService({
      connectionId: serviceConnectionId,
      engineId: engine,
      buildParsedDocument: buildSync,
      pullSchemaSnapshot,
      // A chip filter written bare as `<guard> AND <test>` is one condition.
      foldConditionPair: (text) => isChipFilterPair(text, engineId),
    });
    serviceKeyRef.current = desiredKey;
    // The new service has no open document yet; clear the "already opened"
    // marker so the open effect re-opens it (the else/onTextChange branch
    // throws on an unknown documentId).
    openedRef.current = null;
    registerTabServiceForTests(tabId, serviceRef.current, () =>
      forceUpdate((n) => n + 1),
    );
    documentServices.set(documentId, serviceRef.current);
  }
  const service = serviceRef.current!;

  useEffect(() => {
    if (disposedRef.current) return;
    let cancelled = false;
    const onResolved = () => {
      if (!cancelled && !disposedRef.current) forceUpdate((n) => n + 1);
    };
    const swallowDisposed = (err: unknown) => {
      if (
        err instanceof Error &&
        err.message.includes('service has been disposed')
      ) {
        return;
      }
      throw err;
    };
    if (openedRef.current === null || openedRef.current !== documentId) {
      openedRef.current = documentId;
      void service
        .onOpenDocument({ documentId, text: query, options })
        .then(onResolved, swallowDisposed);
    } else {
      void service
        .onTextChange(documentId, query)
        .then(onResolved, swallowDisposed);
    }
    return () => {
      cancelled = true;
    };
  }, [documentId, query, options, service]);

  // Live schema-snapshot wiring. The database the current snapshot was pulled
  // for: switching the tab's database drops the old snapshot first, so the
  // surface shows "loading" for the new database instead of the previous
  // database's tables (or error).
  const pulledDatabaseRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (pulledDatabaseRef.current !== undefined && pulledDatabaseRef.current !== database) {
      service.setSchemaSnapshot(undefined);
      forceUpdate((n) => n + 1);
    }
    pulledDatabaseRef.current = database;
    if (!connectionId || !database) return undefined;
    // The pull resolves once tables + columns are in; the scheduler follows
    // the rest of the load through the bus (see schemaPullScheduler.ts).
    // Re-render on BOTH outcomes: a rejected pull is recorded on the service
    // (`getSchemaPullError`) and the readiness banner must show it rather than
    // keep saying the schema is loading.
    const scheduler = createSchemaPullScheduler(
      () => service.refreshSchemaSnapshot(database),
      () => {
        if (!disposedRef.current) forceUpdate((n) => n + 1);
      },
    );
    scheduler.request();
    const unsubscribe = subscribeToSchemaBus(connectionId, (event) => {
      if (disposedRef.current) return;
      if (event.type === 'updated' && event.database !== database) return;
      if (event.type === 'updated' || event.type === 'invalidated') {
        scheduler.request();
      }
    });
    // A declaration write (loose FK, JSON column — from any surface or MCP)
    // changes what the snapshot folds in without touching the native schema
    // cache, so it never reaches the bus. Through the SAME scheduler: a write
    // landing mid-pull queues one follow-up pull rather than joining the one
    // that already read the old MetaInfo. Cache-only writes are skipped by the
    // helper — the snapshot folds in no derived cache.
    const unsubscribeMetaInfo = onMetaInfoDeclarationWritten(connectionId, database, () => {
      if (!disposedRef.current) scheduler.request();
    });
    return () => {
      scheduler.cancel();
      unsubscribe();
      unsubscribeMetaInfo();
    };
  }, [service, connectionId, database]);

  // Dispose only on real unmount — NOT on every service swap. Keying this on
  // `[service, …]` made the OLD service's cleanup set the shared `disposedRef`
  // true on each swap (connectionId resolving undefined→real, engine change),
  // and it was never reset, permanently wedging the new service: the open
  // effect bailed (document never opened) and snapshot refreshes never
  // re-rendered (→ "Loading schema…" hung). The old service is already disposed
  // inline in the render body above; here we only dispose the latest service
  // when the component truly unmounts.
  //
  // Re-arm on (re)mount. React StrictMode (dev) mounts → unmounts → remounts:
  // the cleanup below disposed the service and set `disposedRef` true. On the
  // remount `serviceRef` still points at that DEAD service and `disposedRef` is
  // stuck true, so the schema-pull effect bails forever and the snapshot never
  // lands → "Loading schema…" hangs. (SQLite hides this because its in-process
  // pull resolves before the StrictMode unmount; the slow async PG/MSSQL IPC
  // pull always loses the race — and e2e runs a production build with no
  // StrictMode double-mount.) A prior cleanup having run is exactly
  // `disposedRef.current === true` here — re-arm by clearing the service key so
  // the render body rebuilds a LIVE service, and force the render that does it.
  useEffect(() => {
    if (disposedRef.current) {
      disposedRef.current = false;
      serviceKeyRef.current = null;
      forceUpdate((n) => n + 1);
    }
    return () => {
      disposedRef.current = true;
      serviceRef.current?.onDisconnect();
      if (serviceRef.current) unregisterTabServiceForTests(tabId, serviceRef.current);
      // Only our own entry: a surface remounted on the same document has
      // registered its service before this cleanup runs.
      if (documentServices.get(documentId) === serviceRef.current) documentServices.delete(documentId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only; see comment
  }, []);

  // The model adapter must always splice against the LATEST source — chained
  // edit-action calls (e.g. `modifyOrderBy` → remove + add) run two commits
  // synchronously inside one click handler, between which React has not
  // re-rendered. The adapter reads `service.getParsedBuffer(documentId)` on
  // every call so the second commit sees the post-first-commit source.
  const { createModel } = adapter;
  const model = useMemo<MonacoTextModelLike>(
    () => createModel(() => service.getParsedBuffer(documentId)?.parsedDocument.source ?? query),
    [createModel, service, documentId, query],
  );

  // Resolve the scope at the start of the active statement so SELECT / FROM
  // popovers see what the cursor would see. We re-resolve on every render so
  // a snapshot injection (which doesn't go through React state) also picks
  // up the new reachable-tables list. The walk is O(scope-depth × aliases)
  // — cheap relative to the React render itself.
  let scope = null as ReturnType<SqlResolverService['resolveScope']> | null;
  if (statementId) {
    const buf = service.getParsedBuffer(documentId);
    const stmt = buf?.documentStructure.statements.find((s) => s.id === statementId);
    if (stmt) {
      scope = service.resolveScope(documentId, {
        offset: stmt.range.startOffset,
        line: 0,
        column: 0,
      });
    }
  }

  if (!statementId) return { editContext: null, service };

  return {
    service,
    editContext: {
      service,
      documentId,
      statementId,
      tabId,
      model,
      editor: adapter.editor,
      getLiveSource: () => adapter.getLiveSource() ?? query,
      scope,
    },
  };
}

// --- Per-document schema-snapshot registry ---------------------------------
// A live pointer to each mounted surface's current `SchemaSnapshot`, keyed by
// its `documentId` (`${adapter.documentPrefix}${tabId}` — the SQL view's
// compact popover and the Visual view use different prefixes for the same
// tab, so each gets its own entry). Read-only production consumers that need
// "does the condition editor already have a snapshot for this document" —
// e.g. the results grid's header "Add to Filter" availability check, which
// must not enable an item the editor, once it has a snapshot, disables — call
// `getVqeSchemaSnapshotForDocument` instead of re-pulling their own copy, so
// availability and the editor always read the same snapshot.
const documentServices = new Map<string, SqlResolverService>();

export function getVqeSchemaSnapshotForDocument(documentId: string): SchemaSnapshot | undefined {
  return documentServices.get(documentId)?.getSchemaSnapshot();
}

// --- Test-only registry ---------------------------------------------------
// Exposes per-tab Resolver services to E2E specs so tests can seed schema
// snapshots without a live database connection. Production code never
// reads this registry; in non-test builds nothing reads or writes it.
//
// We don't augment the `Window.__TEST_STORES__` type — the canonical
// declaration is in `src/features/sessions/index.ts` and adding new fields
// there would break production session state typing. Instead we read/write
// via a dynamic property on a loosely-typed view of `__TEST_STORES__`.

export type VqeTabServiceEntry = {
  readonly service: SqlResolverService;
  readonly notifyChange: () => void;
};

const VQE_TAB_SERVICES_KEY = 'vqeTabServices' as const;

type LooseTestStores = {
  [VQE_TAB_SERVICES_KEY]?: Map<string, VqeTabServiceEntry>;
} & Record<string, unknown>;

function getStores(): LooseTestStores | null {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stores = (window as any).__TEST_STORES__ as LooseTestStores | undefined;
  return stores ?? null;
}

function registerTabServiceForTests(
  tabId: string,
  service: SqlResolverService,
  notifyChange: () => void,
): void {
  const stores = getStores();
  if (!stores) return;
  if (!stores[VQE_TAB_SERVICES_KEY]) {
    stores[VQE_TAB_SERVICES_KEY] = new Map();
  }
  stores[VQE_TAB_SERVICES_KEY]!.set(tabId, { service, notifyChange });
}

/**
 * Drop the tab's entry only while it still points at `service`: two surfaces
 * of one Query tab (the compact popover and Visual mode) register under the
 * same tab id, and the one unmounting must not remove the one mounting.
 */
function unregisterTabServiceForTests(tabId: string, service: SqlResolverService): void {
  const map = getStores()?.[VQE_TAB_SERVICES_KEY];
  if (map?.get(tabId)?.service === service) map.delete(tabId);
}
